import { formatMoney } from '@theme/utilities';
import { ThemeEvents, CartUpdatedEvent, FormUpdatedEvent } from '@theme/events';

/**
 * 🔴 Message affiché quand une photo choisie n'a jamais été envoyée (l'application de
 * personnalisation n'a pas pris la main sur l'ajout au panier). Sans ce garde-fou, la commande
 * partait avec « C:\fakepath\photo.jpg » à la place de la photo : 44 lignes sur 101 chez Vunika
 * entre juin et septembre 2026.
 */
const PHOTO_NOT_SENT_MESSAGES = {
  fr: "Votre photo n'a pas été prise en compte. Rechargez la page et ajoutez-la à nouveau.",
  en: 'Your photo was not received. Please reload the page and add it again.',
  de: 'Ihr Foto wurde nicht übernommen. Bitte laden Sie die Seite neu und fügen Sie es erneut hinzu.',
  it: 'La tua foto non è stata ricevuta. Ricarica la pagina e aggiungila di nuovo.',
  es: 'Tu foto no se ha recibido. Vuelve a cargar la página y añádela de nuevo.',
  nl: 'Je foto is niet ontvangen. Laad de pagina opnieuw en voeg hem opnieuw toe.',
  pl: 'Twoje zdjęcie nie zostało przesłane. Odśwież stronę i dodaj je ponownie.',
  'pt-BR': 'Sua foto não foi recebida. Recarregue a página e adicione-a novamente.',
  'pt-PT': 'A sua foto não foi recebida. Recarregue a página e adicione-a novamente.',
  pt: 'A sua foto não foi recebida. Recarregue a página e adicione-a novamente.',
  cs: 'Vaše fotografie nebyla přijata. Obnovte stránku a přidejte ji znovu.',
  da: 'Dit foto blev ikke modtaget. Genindlæs siden, og tilføj det igen.',
  el: 'Η φωτογραφία σας δεν ελήφθη. Ανανεώστε τη σελίδα και προσθέστε την ξανά.',
  fi: 'Kuvaasi ei vastaanotettu. Lataa sivu uudelleen ja lisää kuva uudestaan.',
  he: 'התמונה לא התקבלה. יש לטעון מחדש את הדף ולהוסיף אותה שוב.',
  hu: 'A fénykép nem érkezett meg. Töltse újra az oldalt, és adja hozzá ismét.',
  ja: '写真を受け取れませんでした。ページを再読み込みして、もう一度追加してください。',
  nb: 'Bildet ditt ble ikke mottatt. Last inn siden på nytt og legg det til igjen.',
  ro: 'Fotografia nu a fost primită. Reîncărcați pagina și adăugați-o din nou.',
  sk: 'Vaša fotografia nebola prijatá. Obnovte stránku a pridajte ju znova.',
  sv: 'Ditt foto togs inte emot. Ladda om sidan och lägg till det igen.',
  ar: 'لم يتم استلام صورتك. يرجى إعادة تحميل الصفحة وإضافتها مرة أخرى.',
};

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.addToCartButton = null;
    this.addToCartButtonContainer = null;
    this.addToCartBehavior = null;
    this.toggleCrossSells = null;
    this.quantityBreaks = null;
    this.subscription = null;
    this.quantitySelectorInput = null;

    // Handlers bindés pour pouvoir les retirer correctement
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onVariantSelected = this.onVariantSelected.bind(this);
    this.onFormUpdated = this.onFormUpdated.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form[action$="/cart/add"]');
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.addToCartButtonContainer = this.querySelector('[data-ref="add-to-cart-button-container"]');
    this.addToCartBehavior = document.querySelector('cart-icon')?.addToCartBehavior || 'open_cart';
    this.toggleCrossSells = this.querySelector('[data-ref="toggle-cross-sells"]');
    this.quantityBreaks = this.querySelector('[data-ref="quantity-breaks"]');
    this.subscription = this.querySelector('[data-ref="subscription"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="product-quantity-selector"] input[name="quantity"]');

    this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected);
    this.addEventListener(ThemeEvents.variantUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.toggleCrossSellsUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.onFormUpdated);
    this.quantitySelectorInput?.addEventListener('change', this.onFormUpdated);

    /**
     * Quand le formulaire a finit de charger, on déclenche une mise à jour de prix pour éviter les problèmes de prix
     * si la quantité par défaut n'est pas 1 ou que l'abonnement est pré-sélectionné
     */
    document.addEventListener('DOMContentLoaded', () => this.onFormUpdated(), { once: true });
    this.addEventListener('quickAddContentLoaded', () => this.onFormUpdated(), { once: true });
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener(ThemeEvents.variantSelected, this.onVariantSelected);
    this.removeEventListener(ThemeEvents.variantUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.toggleCrossSellsUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.onFormUpdated);
    this.quantitySelectorInput?.removeEventListener('change', this.onFormUpdated);
  }

  onVariantSelected() {
    this.addToCartButton.disabled = true;
  }

  onFormUpdated(event) {
    // On récupère le DOM actuel du bouton d'ajout au panier
    const addToCartButtonContainer = this.addToCartButtonContainer;

    // Le event.detail est null si l'évenement de mise à jour est déclenché par les cross-sells à activer et non par les variantes, dans ce cas là on utilise le DOM actuel
    let newAddToCartButtonContainer = addToCartButtonContainer;
    if (event && event.detail != null) {
      newAddToCartButtonContainer = event.detail.data.html.querySelector('[data-ref="add-to-cart-button-container"]');
    }

    if (!newAddToCartButtonContainer || !addToCartButtonContainer) return;

    // On remplace le DOM actuel par le nouveau DOM
    if (addToCartButtonContainer !== newAddToCartButtonContainer) {
      this.#keepLiveAddToCartButton(newAddToCartButtonContainer);
      addToCartButtonContainer.replaceWith(newAddToCartButtonContainer);
      this.#bindQuantitySelectorInput();
    }

    // On réassigne les nouvelles valeurs du bouton pour le reste du fonctionnement du composant
    this.addToCartButton = newAddToCartButtonContainer.querySelector('[data-ref="add-to-cart-button"]');
    this.addToCartButtonContainer = newAddToCartButtonContainer;

    // Une photo est en cours d'envoi par l'application de personnalisation : le bouton reste grisé, elle le réactive à la fin
    if (this.#isUploadingFile()) {
      this.addToCartButton.disabled = true;
    }

    // On récupère les prix totaux du formulaire (incluant le produit de base, sa quantité, les cross-sells à activer, etc.)
    const newTotalPrice = this.getCurrentTotalPrice().price;
    const newTotalCompareAtPrice = this.getCurrentTotalPrice().compare_at_price;

    // On met à jour les prix dans le nouveau DOM à insérer
    const priceElement = this.addToCartButtonContainer.querySelector('[data-ref="price"]');
    if (priceElement != null) {
      priceElement.innerHTML = formatMoney(newTotalPrice);
    }

    const compareAtPriceElement = this.addToCartButtonContainer.querySelector('[data-ref="compare-at-price"]');
    if (compareAtPriceElement != null) {
      compareAtPriceElement.innerHTML = '';
      if (newTotalCompareAtPrice > newTotalPrice) {
        compareAtPriceElement.innerHTML = formatMoney(newTotalCompareAtPrice);
      }
    }

    this.dispatchEvent(new FormUpdatedEvent(this.getCurrentTotalPrice()));
  }

  /**
   * 🔴 On garde le bouton d'ajout au panier VIVANT quand on remplace son conteneur.
   * Les applications de personnalisation écoutent le clic sur CE bouton (en phase de capture) pour
   * envoyer la photo et poser leurs champs avant l'ajout. Remplacer le bouton par celui du nouveau
   * rendu laissait leur écouteur sur un bouton détaché : après un changement de variante, le thème
   * ajoutait seul au panier et la photo partait en « C:\fakepath\… ». On reprend donc les attributs
   * et le contenu du nouveau bouton (libellé, disabled si la variante est épuisée…) sur l'ancien,
   * puis on met l'ancien à sa place dans le nouveau conteneur.
   */
  #keepLiveAddToCartButton(newContainer) {
    const liveButton = this.addToCartButton;
    const freshButton = newContainer.querySelector('[data-ref="add-to-cart-button"]');
    if (!liveButton || !freshButton || liveButton === freshButton) return;

    for (const { name } of Array.from(liveButton.attributes)) {
      if (!freshButton.hasAttribute(name)) liveButton.removeAttribute(name);
    }
    for (const { name, value } of Array.from(freshButton.attributes)) {
      liveButton.setAttribute(name, value);
    }
    liveButton.replaceChildren(...Array.from(freshButton.childNodes));
    freshButton.replaceWith(liveButton);
  }

  // Le sélecteur de quantité vit dans le conteneur remplacé : on écoute le nouveau champ
  #bindQuantitySelectorInput() {
    const input = this.querySelector('[data-ref="product-quantity-selector"] input[name="quantity"]');
    if (input === this.quantitySelectorInput) return;
    this.quantitySelectorInput?.removeEventListener('change', this.onFormUpdated);
    this.quantitySelectorInput = input;
    this.quantitySelectorInput?.addEventListener('change', this.onFormUpdated);
  }

  #isUploadingFile() {
    return this.form?.querySelector('input[type="file"][disabled]') != null;
  }

  /**
   * Un champ `properties[...]` n'est envoyé que s'il serait envoyé par un vrai formulaire, et s'il a
   * une valeur : jamais un champ désactivé, jamais un bouton radio ou une case non cochés (avant, le
   * dernier bouton radio écrasait le choix du client, d'où « 5 prénoms » sur des commandes à 2),
   * jamais un champ fichier (sa valeur est le faux chemin « C:\fakepath\… », pas la photo).
   */
  #isSendableProperty(input) {
    if (input.disabled) return false;
    if (input.type === 'file') return false;
    if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return false;
    return String(input.value ?? '').trim() !== '';
  }

  #propertyKey(name) {
    return name.slice('properties['.length).replace(/\]\[\]$/, '').replace(/\]$/, '');
  }

  #collectProperties() {
    const properties = {};
    const fromCheckbox = new Set();

    this.form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach((input) => {
      if (!this.#isSendableProperty(input)) return;
      const key = this.#propertyKey(input.name);
      // Plusieurs cases cochées sous le même nom : on les garde toutes
      if (input.type === 'checkbox' && fromCheckbox.has(key) && key in properties) {
        properties[key] = `${properties[key]}, ${input.value}`;
        return;
      }
      properties[key] = input.value;
      if (input.type === 'checkbox') fromCheckbox.add(key);
      else fromCheckbox.delete(key);
    });

    return this.#customizerPropertiesInFrench(properties);
  }

  /**
   * 🔴 Les noms des champs de commande restent en FRANÇAIS, quelle que soit la langue du visiteur.
   * L'application de personnalisation (Eazy Product Options / Mandolly) traduit les noms et les choix
   * avec son propre dictionnaire juste avant l'ajout (« Texte 2 » devient « Tekst 2 », « Numéro »
   * devient « Hausnummer »…) : l'atelier recevait des commandes dans 21 langues, avec des erreurs
   * (numéro et rue inversés en arabe et en hébreu). On fait le chemin inverse avec le même
   * dictionnaire, en ne retenant que les noms et les choix réellement présents sur la fiche ; une
   * traduction ambiguë reste telle quelle.
   */
  #customizerPropertiesInFrench(properties) {
    const dictionary = window.MEPCSettings?.translation;
    const locale = window.Shopify?.locale;
    if (!dictionary || !locale || locale.startsWith('fr')) return properties;

    const translate = (text) => {
      let translated = text;
      for (const language of Object.keys(dictionary)) {
        const value = dictionary[language]?.values?.[text];
        if (language.includes(locale) && typeof value === 'string' && value.trim() !== '') translated = value;
      }
      return translated;
    };

    // Les noms et les choix français viennent de la configuration de l'application publiée dans la page :
    // au moment de l'ajout, elle a déjà retiré ses propres champs du formulaire.
    const titles = new Set();
    const choices = new Set();
    const addOptionSet = (optionSet) => {
      for (const option of optionSet?.optionList || []) {
        if (option?.optionTitle) titles.add(option.optionTitle);
        for (const choice of option?.options || []) {
          if (choice?.value) choices.add(choice.value);
        }
      }
    };
    for (const name of ['MEPCProductOptions', 'MEPCOptions', 'MEPCOptionsV1', 'MEPCUniversalOption', 'MEPCCollectionOptions']) {
      addOptionSet(window[name]);
    }
    for (const optionSet of Object.values(window.MEPCVariantOptions || {})) {
      addOptionSet(optionSet);
    }
    this.form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach((input) => {
      if (input.type === 'hidden') return;
      titles.add(this.#propertyKey(input.name));
      if (input.type === 'radio' || input.type === 'checkbox') choices.add(input.value);
      if (input.tagName === 'SELECT') {
        for (const option of Array.from(input.options)) {
          if (option.value) choices.add(option.value);
        }
      }
    });

    const backToFrench = (text, candidates) => {
      if (candidates.has(text)) return text;
      const trimmed = text.trim();
      if (candidates.has(trimmed)) return trimmed;
      const matches = Array.from(candidates).filter((candidate) => translate(candidate).trim() === trimmed);
      return matches.length === 1 ? matches[0] : text;
    };

    const keyToFrench = (key) => {
      const french = backToFrench(key, titles);
      if (french !== key) return french;
      // Envoi de plusieurs fichiers : « Votre image-1 », « Votre image-2 »…
      const numbered = key.match(/^(.*)-(\d+)$/);
      if (!numbered) return key;
      const base = backToFrench(numbered[1], titles);
      return base === numbered[1] ? key : `${base}-${numbered[2]}`;
    };

    const valueToFrench = (value) => {
      if (typeof value !== 'string' || /^https?:\/\//i.test(value.trim())) return value;
      const whole = backToFrench(value, choices);
      if (whole !== value) return whole;
      const pieces = value.split(',');
      const frenchPieces = pieces.map((piece) => backToFrench(piece.trim(), choices));
      const changed = frenchPieces.some((piece, index) => piece !== pieces[index].trim());
      return changed ? frenchPieces.join(', ') : value;
    };

    const french = {};
    for (const [key, value] of Object.entries(properties)) {
      if (key.startsWith('_')) {
        french[key] = value;
        continue;
      }
      french[keyToFrench(key)] = valueToFrench(value);
    }
    return french;
  }

  /**
   * Une photo choisie dans un champ fichier doit arriver dans la commande sous forme de lien. Si ce
   * n'est pas le cas, l'application n'a pas envoyé la photo : on n'ajoute pas au panier.
   */
  #hasUnsentPhoto(properties) {
    const fileInputs = this.form.querySelectorAll('input[type="file"][name^="properties["]');
    for (const input of Array.from(fileInputs)) {
      if (!input.files || input.files.length === 0) continue;
      const key = this.#propertyKey(input.name);
      const sent = Object.entries(properties).some(
        ([name, value]) => (name === key || name.startsWith(`${key}-`)) && /^https?:\/\//i.test(String(value).trim()),
      );
      if (!sent) return true;
    }
    return false;
  }

  #localizedMessage(messages) {
    const locale = window.Shopify?.locale || document.documentElement.lang || 'fr';
    return messages[locale] || messages[locale.split('-')[0]] || messages.en;
  }

  #showError(message) {
    const escaped = String(message).replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`);
    document.dispatchEvent(
      new CustomEvent('toast:open', {
        detail: { type: 'error', message: escaped },
      }),
    );
  }

  handleSubmit(event) {
    event.preventDefault();

    // Une photo est encore en cours d'envoi : l'ajout attendra la fin de l'envoi
    if (this.#isUploadingFile()) return;

    // Un bloc de personnalisation peut refuser l'ajout (champ obligatoire vide, envoi en cours…)
    const validateEvent = new CustomEvent('product-form:validate', { bubbles: true, cancelable: true });
    if (!this.dispatchEvent(validateEvent)) return;

    const properties = this.#collectProperties();
    if (this.#hasUnsentPhoto(properties)) {
      this.#showError(this.#localizedMessage(PHOTO_NOT_SENT_MESSAGES));
      return;
    }

    this.addToCartButton.classList.add('is-loading');

    let items = [];

    const hasItemsInputs = this.form?.querySelector('input[name^="items["], select[name^="items["], textarea[name^="items["]') !== null;

    // Cas avec plusieurs produits avec format items[0][id], items[0][quantity], items[0][properties][color] (parfois utilisé par des applications de bundle et autre)
    if (hasItemsInputs) {
      const itemsData = {};

      this.form.querySelectorAll('input[name^="items["], select[name^="items["], textarea[name^="items["]').forEach((input) => {
        const name = input.name;

        // Extraire l'index: items[0] -> 0
        const indexMatch = name.match(/items\[(\d+)\]/);
        if (!indexMatch) return;

        const index = indexMatch[1];

        // Créer l'objet pour cet index s'il n'existe pas
        if (!itemsData[index]) {
          itemsData[index] = {};
        }

        // Déterminer quel champ on traite
        if (name.includes('[id]')) {
          itemsData[index].id = input.value;
        } else if (name.includes('[quantity]')) {
          itemsData[index].quantity = parseInt(input.value, 10) || 1;
        } else if (name.includes('[selling_plan]')) {
          // Support des abonnements (selling plans) pour chaque item
          if (input.value) {
            itemsData[index].selling_plan = input.value;
          }
        } else if (name.includes('[properties]')) {
          if (!this.#isSendableProperty(input)) return;
          // Extraire le nom de la propriété: items[0][properties][color] -> color
          const propertyMatch = name.match(/\[properties\]\[([^\]]+)\]/);
          if (propertyMatch) {
            if (!itemsData[index].properties) {
              itemsData[index].properties = {};
            }
            itemsData[index].properties[propertyMatch[1]] = input.value;
          }
        }
      });

      // Convertir l'objet en array
      for (const key in itemsData) {
        items.push(itemsData[key]);
      }
    }

    if (this.quantityBreaks?.isActive?.() && this.quantityBreaks.isVariantSelectorActivated()) {
      /** Quand le mode "afficher les variantes" est activé sur le quantity break,
       * on ne veut pas prendre en compte l'abonnement, les variantes, et la quantité par défaut
       * car ces blocs sont masqués (incompatible avec le mode "afficher les variantes").
       * On récupère simplement les variantes sélectionnées dans le quantity break et leur quantité.
       */
      const quantityBreaksItems = this.quantityBreaks.getSelectedItems();

      if (Object.keys(properties).length > 0) {
        for (const qbItem of quantityBreaksItems) {
          qbItem.properties = { ...properties, ...qbItem.properties };
        }
      }

      items = [...items, ...quantityBreaksItems];
    } else {
      let quantityBreaksItems = [];
      let item;

      if (this.quantityBreaks?.isActive?.()) {
        // On récupère tous les items du quantity break (produit principal + éventuels cross-sells)
        quantityBreaksItems = this.quantityBreaks.getSelectedItems();
        // On ajoute à la sélection uniquement le produit principal (les cross-sells sont ajoutés après)
        item = quantityBreaksItems[0];
      } else {
        item = {
          id: this.form.querySelector('input[name="id"]').value,
          quantity: parseInt(this.form.querySelector('input[name="quantity"]')?.value, 10) || 1,
        };
      }

      // Ajouter les properties sur l'item principal seulement s'il y en a
      if (Object.keys(properties).length > 0) {
        item.properties = properties;
      }

      // Support des abonnements (selling plans)
      const sellingPlanInput = this.form.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
      if (sellingPlanInput?.value) {
        item.selling_plan = sellingPlanInput.value;
      }

      items.push(item);

      // Ajouter les cross-sells du quantity break (éléments après le premier)
      if (quantityBreaksItems.length > 1) {
        items = [...items, ...quantityBreaksItems.slice(1)];
      }
    }

    // On ajoute les cross-sells à cocher dans la sélection
    if (this.toggleCrossSells) {
      items = [...items, ...this.toggleCrossSells.getSelectedItems().items];
    }

    // Grouper les items par id (somme des quantités) pour que les Web Pixels Shopify déclenchent product_added_to_cart (une ligne par variante)
    items = this.#mergeItemsById(items);

    // On récupère la quantité totale à ajouter au panier pour mettre à jour le compteur du panier
    let quantityAddedToCart = items.reduce((acc, item) => acc + item.quantity, 0);

    const sections = [];
    if (document.querySelector('cart-drawer')) {
      sections.push(document.querySelector('cart-drawer').dataset.sectionId);
    }

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: items,
        sections: sections.join(','),
      }),
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        // Un refus du panier (stock, limite…) arrive en 422 : on l'affiche au lieu d'ouvrir un panier inchangé
        if (!response.ok) {
          throw new Error(body.description || body.message || response.statusText);
        }
        return body;
      })
      .then((response) => {
        const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

        const itemCountToAdd = quantityAddedToCart || 1;

        let newItemCount;
        if (currentItemCount != null) {
          newItemCount = currentItemCount + itemCountToAdd;
        }

        // On dispatch l'événement de mise à jour du panier
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: newItemCount,
              source: 'product-form',
              sections: response.sections,
            },
          ),
        );

        this.addToCartButton.classList.remove('is-loading');
      })
      .then(() => {
        if (this.addToCartBehavior == 'notification') {
          document.dispatchEvent(
            new CustomEvent('toast:open', {
              detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
            }),
          );
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        this.addToCartButton.classList.remove('is-loading');
        this.#showError(error?.message || error);
      });
  }

  /**
   * Fusionne les items ayant le même id en additionnant les quantités.
   * Permet aux Web Pixels Shopify de déclencher product_added_to_cart (une ligne par variante).
   * @param {Array<{ id: string, quantity: number, ... }>} items
   * @returns {Array<{ id: string, quantity: number, ... }>}
   */
  #mergeItemsById(items) {
    const byId = new Map();
    for (const item of items) {
      const key = String(item.id);
      if (!byId.has(key)) {
        byId.set(key, { ...item, quantity: 0 });
      }
      const merged = byId.get(key);
      merged.quantity += parseInt(item.quantity, 10) || 1;
    }
    return Array.from(byId.values());
  }

  /**
   * Permet de récupérer les prix totaux du formulaire
   * (incluant le produit de base, sa quantité, les cross-sells à activer, l'abonnement, et les réductions sur la quantité)
   * @returns {Object} { price: number, compare_at_price: number, difference_price: number }
   */
  getCurrentTotalPrice() {
    // Le prix de base du produit est stocké dans le dataset du bouton d'ajout au panier
    let basePrice = parseInt(this.addToCartButtonContainer.dataset.price);
    let baseCompareAtPrice = basePrice;
    if (this.addToCartButtonContainer.dataset.compareAtPrice) {
      baseCompareAtPrice = parseInt(this.addToCartButtonContainer.dataset.compareAtPrice);
    }

    let newPrice = basePrice;
    let newCompareAtPrice = baseCompareAtPrice;
    let quantity = 1;

    if (this.quantityBreaks?.isActive?.()) {
      const prices = this.quantityBreaks.getPrices();
      const discount = this.quantityBreaks.getDiscount();
      const priceAfterQB = parseInt(prices.price);
      const compareAtPrice = parseInt(prices.compareAtPrice);

      // Récupérer la quantité sélectionnée
      const selectedQuantity = parseInt(this.quantityBreaks.getSelectedQuantity()?.dataset?.quantity) || 1;
      quantity = this.quantityBreaks.getSelectedItems().reduce((acc, item) => acc + item.quantity, 0);

      // Calculer le prix AVANT réduction QB (prix unitaire × quantité, comme dans subscription.js)
      const priceBeforeQB = basePrice * selectedQuantity;
      newCompareAtPrice = baseCompareAtPrice * selectedQuantity;

      // Récupérer les prix des cross-sells QB
      let crossSellPrice = 0;
      let crossSellCompareAtPrice = 0;
      if (typeof this.quantityBreaks.getCrossSellPrices === 'function') {
        const crossSellPrices = this.quantityBreaks.getCrossSellPrices();
        crossSellPrice = parseInt(crossSellPrices.price) || 0;
        crossSellCompareAtPrice = parseInt(crossSellPrices.compareAtPrice) || crossSellPrice;
      }

      if (this.subscription && this.subscription.isSubscriptionSelected()) {
        // Ordre Shopify : Abonnement → puis QB
        // 1. Appliquer la réduction abonnement sur le prix avant QB
        let priceWithAbo = this.subscription.calculateAdjustedPrice(priceBeforeQB, selectedQuantity);
        // 2. Appliquer la réduction QB
        if (discount.type === 'percentage' && discount.percentage > 0) {
          newPrice = Math.round((priceWithAbo * (100 - discount.percentage)) / 100);
        } else if (discount.value > 0) {
          newPrice = Math.max(0, priceWithAbo - discount.value);
        } else {
          newPrice = priceWithAbo;
        }
      } else {
        // Achat unique : prix après QB directement
        newPrice = priceAfterQB;
        newCompareAtPrice = compareAtPrice;
      }

      // Ajouter les cross-sells du quantity break (après les réductions, pas de réduction sur les cross-sells)
      newPrice += crossSellPrice;
      newCompareAtPrice += crossSellCompareAtPrice;
    } else {
      // Cas sans quantity breaks : récupérer la quantité du sélecteur ou 1 par défaut
      quantity = parseInt(this.quantitySelectorInput?.value) || 1;
      newPrice = newPrice * quantity;
      newCompareAtPrice = newCompareAtPrice * quantity;

      // Appliquer l'abonnement
      if (this.subscription && this.subscription.isSubscriptionSelected()) {
        newPrice = this.subscription.calculateAdjustedPrice(newPrice, quantity);
      }
    }

    // Gestion des cross-sells (ajoutés après l'abonnement car ils ont leur propre prix)
    if (this.toggleCrossSells && typeof this.toggleCrossSells.getSelectedItems === 'function') {
      newPrice += parseInt(this.toggleCrossSells.getSelectedItems().total_price);
      newCompareAtPrice += parseInt(this.toggleCrossSells.getSelectedItems().total_compare_at_price);
    }

    // S'assurer que le prix ne soit jamais négatif
    newPrice = Math.max(0, newPrice);
    const differencePrice = newCompareAtPrice - newPrice;

    return {
      price: newPrice,
      compare_at_price: newCompareAtPrice,
      difference_price: differencePrice,
    };
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
